import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { PayCorrectionResult } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionOutput';
import { PayCorrectionAdjustedEmployeeSalaries } from 'libs/end-to-end/src/pay-correction/types/PayCorrectionAdjustedEmployeeSalaries';
import { formatCurrency } from 'apps/sigma2-ui/src/lib/number-formatting';
import { useIntersectionObserver } from 'apps/sigma2-ui/src/lib/hooks/useIntersectionObserver';
import { useInfiniteScrollPagination } from 'apps/sigma2-ui/src/lib/hooks/useInfiniteScrollPagination';
import { useTableSorting } from 'apps/sigma2-ui/src/lib/hooks/useTableSorting';
import { SortableTableHeaderCell } from 'apps/sigma2-ui/src/components/ui/Table';
import { TABLE_HEADER_OFFSET_HEIGHT } from 'apps/sigma2-ui/src/components/ui/Table';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { TablePaginationFooter } from 'apps/sigma2-ui/src/components/ui/Table';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { LevelDefinitionButton } from 'apps/sigma2-ui/src/components/LevelDefinitionButton';
import { CheckmarkAcceptableBadgeSvg } from 'apps/sigma2-ui/src/components/svg/CheckmarkAcceptableBadge';
import { SelectAllCheckbox } from 'apps/sigma2-ui/src/components/ui/SelectAllCheckbox';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { SetterWithReducer } from 'libs/end-to-end/src/ui-types';
import { getPayCorrectionAdjustedSalaryByEmployeeId } from 'apps/sigma2-ui/src/pay-correction/libs/getPayCorrectionAdjustedSalaryByEmployeeId';
import { useToggle } from 'apps/sigma2-ui/src/lib/hooks/useToggle';
import { IconEditSvg } from 'apps/sigma2-ui/src/components/svg/IconEdit';

export interface PayCorrectionEmployeeItem extends PayCorrectionResult {
  checked: boolean;
  id: string;
}

interface PayCorrectionTableProps {
  items: PayCorrectionEmployeeItem[];
  isLoading: boolean;
  setAdjustedSalaries: SetterWithReducer<PayCorrectionAdjustedEmployeeSalaries>;
  adjustedSalaries: PayCorrectionAdjustedEmployeeSalaries;
  dimensionTableHeaderCell: React.ReactNode;
  uncheckedEmployees: string[];
  setUncheckedEmployees: Setter<string[]>;
  selectedDimensionLevelFilter: string | null;
  onDimensionLevelFilterChange: Setter<string | null> | null;
}

const TruncatedText: React.FC<{
  children: React.ReactNode;
  widthClassName: string;
}> = ({ children, widthClassName }) => {
  return (
    <div
      className={cn(
        'overflow-ellipsis overflow-hidden whitespace-nowrap',
        widthClassName,
      )}
      onMouseOver={(e) => {
        // If the text is truncated, show a tooltip with the full text
        const target = e.target as HTMLDivElement;
        if (target.scrollWidth > target.clientWidth) {
          target.title = children as string;
        }
      }}
    >
      {children}
    </div>
  );
};

const isAcceptableAnnualSalary = ({
  adjustedSalary,
  recommendedSalary,
}: {
  adjustedSalary: number;
  recommendedSalary: number;
}): boolean => {
  return adjustedSalary >= recommendedSalary;
};

export const PayCorrectionTable: React.FC<PayCorrectionTableProps> = ({
  items,
  isLoading,
  setAdjustedSalaries,
  adjustedSalaries,
  dimensionTableHeaderCell,
  uncheckedEmployees,
  selectedDimensionLevelFilter,
  setUncheckedEmployees,
  onDimensionLevelFilterChange,
}) => {
  const { getLevelByColumnValue } = useDimensionConfig();

  const itemsWithAdjustedSalaries = React.useMemo(() => {
    return items.map((item) => {
      const adjusted_salary = getPayCorrectionAdjustedSalaryByEmployeeId({
        adjustedSalaries,
        employeeid: item.id,
        recommendedSalary: item.recommended_salary,
      });

      return {
        ...item,
        adjusted_salary,
      };
    });
  }, [items, adjustedSalaries]);

  const allAreSelected = useMemo(
    () =>
      itemsWithAdjustedSalaries.every(
        (item) => !uncheckedEmployees.includes(item.id),
      ),
    [itemsWithAdjustedSalaries, uncheckedEmployees],
  );

  const selectAll = useCallback(() => {
    // Loop through itemsWithAdjustedSalaries and remove them from the set
    const newUncheckedEmployees: string[] = [...uncheckedEmployees];

    for (const item of itemsWithAdjustedSalaries) {
      if (uncheckedEmployees.includes(item.id)) {
        newUncheckedEmployees.splice(newUncheckedEmployees.indexOf(item.id), 1);
      }
    }

    setUncheckedEmployees(newUncheckedEmployees);
  }, [itemsWithAdjustedSalaries, setUncheckedEmployees, uncheckedEmployees]);

  const deselectAll = useCallback(() => {
    // Loop through itemsWithAdjustedSalaries and add them to the set
    const newUncheckedEmployees: string[] = [...uncheckedEmployees];

    for (const item of itemsWithAdjustedSalaries) {
      if (!uncheckedEmployees.includes(item.id)) {
        newUncheckedEmployees.push(item.id);
      }
    }

    setUncheckedEmployees(newUncheckedEmployees);
  }, [itemsWithAdjustedSalaries, setUncheckedEmployees, uncheckedEmployees]);

  const { onSortChange, sortedItems, getSortingInfo } = useTableSorting({
    initialSortedTableColumns: [{ id: 'salary', direction: 'desc' }],
    items: itemsWithAdjustedSalaries,
  });

  const { paginatedItems, onIntersect, doneIntersecting } =
    useInfiniteScrollPagination({
      limit: 10,
      items: sortedItems,
    });

  const [
    lastRowRef,
    { isIntersecting: isLoadingMore, rootRef: tableContainerRef },
  ] = useIntersectionObserver<HTMLTableRowElement, HTMLDivElement>({
    onIntersectSync: onIntersect,
    options: { rootMargin: TABLE_HEADER_OFFSET_HEIGHT },
  });

  const [isInEditMode, toggleIsInEditMode] = useToggle({ initialState: false });

  return (
    <Table
      items={paginatedItems}
      refs={{ lastRow: lastRowRef, tableContainer: tableContainerRef }}
      classNames={{
        tableContainer: 'max-h-[50vh] min-h-[50vh] overflow-y-scroll',
      }}
      isLoading={isLoading || (isLoadingMore && !doneIntersecting)}
      footer={
        items.length > 0 && (
          <TablePaginationFooter
            numTotalItems={items.length}
            numVisibleItems={paginatedItems.length}
          />
        )
      }
      columnDefinitions={[
        {
          id: 'selection',
          header: () => (
            <SelectAllCheckbox
              checked={allAreSelected}
              onChange={allAreSelected ? deselectAll : selectAll}
            >
              {/* Since we don't render a label here in the UI, but want it to be accessible */}
              <span className="hidden">Select all employees</span>
            </SelectAllCheckbox>
          ),
          cell: ({ item }) => {
            const isChecked = !uncheckedEmployees.includes(item.id);

            return (
              <S2Checkbox
                checked={isChecked}
                disabled={false}
                onChange={() => {
                  if (isChecked) {
                    setUncheckedEmployees(
                      uncheckedEmployees.filter((id) => id !== item.id),
                    );
                  } else {
                    setUncheckedEmployees([...uncheckedEmployees, item.id]);
                  }
                }}
              >
                <span className="hidden">
                  Select employee with ID {item.id}
                </span>
              </S2Checkbox>
            );
          },
        },
        {
          id: 'id',
          header: () => 'ID',
          cell: ({ item }) => {
            return item.id;
          },
        },
        {
          id: 'role',
          header: () => (
            <SortableTableHeaderCell
              id="role"
              {...getSortingInfo({ id: 'role' })}
              onSortChange={onSortChange}
            >
              Role
            </SortableTableHeaderCell>
          ),
          cell: ({ item }) => {
            return (
              <TruncatedText widthClassName="w-48">
                <strong>{item.job_title ?? 'N/A'}</strong>
              </TruncatedText>
            );
          },
        },
        {
          id: 'dimensions',
          header: () => {
            return dimensionTableHeaderCell;
          },
          cell: ({ item }) => {
            if (item.gender === null && item.race === null) {
              return <p>-</p>;
            }

            const visibleLevels: { [key: string]: string | null } = {
              Gender: item.gender,
              Race: item.race,
            };

            return (
              <Spacing
                direction="horizontal"
                size="xs"
                className="min-w-40 flex-wrap"
              >
                {Object.entries(visibleLevels).map((visibleLevel) => {
                  const [dimension, level] = visibleLevel;

                  if (!level) {
                    return null;
                  }

                  const levelDefinition = getLevelByColumnValue(
                    dimension,
                    level,
                  );

                  if (!levelDefinition) {
                    return null;
                  }

                  const isAlreadySelected =
                    selectedDimensionLevelFilter === level;

                  return (
                    level && (
                      <div
                        onMouseOver={() => {
                          onDimensionLevelFilterChange?.(level);
                        }}
                        onMouseOut={() => {
                          onDimensionLevelFilterChange?.(null);
                        }}
                      >
                        <LevelDefinitionButton
                          key={level}
                          selected={isAlreadySelected}
                          disabled={true}
                          levelDefinition={levelDefinition}
                          icon
                        />
                      </div>
                    )
                  );
                })}
              </Spacing>
            );
          },
        },
        {
          id: 'long_perf_review',
          header: () => (
            <SortableTableHeaderCell
              id="long_perf_review"
              {...getSortingInfo({ id: 'long_perf_review' })}
              onSortChange={onSortChange}
            >
              Performance
            </SortableTableHeaderCell>
          ),
          cell: ({ item }) => {
            return (
              <TruncatedText widthClassName="w-28">
                {item.long_perf_review}
              </TruncatedText>
            );
          },
        },
        {
          id: 'long_experience',
          header: () => (
            <SortableTableHeaderCell
              id="long_experience"
              {...getSortingInfo({ id: 'long_experience' })}
              onSortChange={onSortChange}
            >
              Exp.
            </SortableTableHeaderCell>
          ),
          cell: ({ item: { long_experience } }) => {
            if (!long_experience) {
              return <span className="whitespace-nowrap">N/A</span>;
            } else if (long_experience === 1) {
              return <span className="whitespace-nowrap">{'<'} 1 year</span>;
            } else {
              return (
                <span className="whitespace-nowrap">
                  {long_experience} years
                </span>
              );
            }
          },
        },
        {
          id: 'salary',
          header: () => (
            <SortableTableHeaderCell
              id="salary"
              {...getSortingInfo({ id: 'salary' })}
              onSortChange={onSortChange}
            >
              Current Salary
            </SortableTableHeaderCell>
          ),
          cell: ({ item }) => {
            return <span>{formatCurrency(item.salary)}/yr</span>;
          },
        },
        {
          id: 'recommended_salary',
          header: () => (
            <SortableTableHeaderCell
              id="recommended_salary"
              {...getSortingInfo({ id: 'recommended_salary' })}
              onSortChange={onSortChange}
            >
              Recommended
            </SortableTableHeaderCell>
          ),
          cell: ({ item }) => {
            if (
              isAcceptableAnnualSalary({
                adjustedSalary: adjustedSalaries[item.id] ?? item.salary,
                recommendedSalary: item.recommended_salary,
              })
            ) {
              return (
                <Spacing direction="horizontal" size="xs">
                  <span>Acceptable</span>

                  <CheckmarkAcceptableBadgeSvg
                    svg={{ className: 'w-5 h-5 text-green-600' }}
                    wantsCurrentColor
                  />
                </Spacing>
              );
            }

            return (
              <span>
                +{formatCurrency(item.recommended_salary - item.salary)}/yr
              </span>
            );
          },
        },
        {
          id: 'adjusted_salary',
          header: () => (
            <div className="space-x-1 flex items-center">
              <span>Adjusted Salary</span>

              <button onClick={toggleIsInEditMode}>
                <IconEditSvg svg={{ className: 'w-4 h-4 text-gray-800' }} />
              </button>
            </div>
          ),
          cell: ({ item }) => {
            const isAcceptable = isAcceptableAnnualSalary({
              recommendedSalary: item.recommended_salary,
              adjustedSalary: item.adjusted_salary,
            });

            const adjustedSalary = Math.round(item.adjusted_salary);

            return (
              <div
                className={cn('px-1 flex flex-row gap-1 w-32 items-center', {})}
              >
                {isInEditMode ? (
                  <div className="flex items-center space-x-1">
                    <label htmlFor={item.employeeid} className="hidden">
                      Adjusted salary
                    </label>

                    <span className="font-semibold text-gray-600">$</span>

                    <input
                      id={item.employeeid}
                      type="number"
                      value={adjustedSalary || undefined}
                      className={cn(
                        'form-input rounded border-gray-200 border px-2 py-1 max-w-[6.5rem] font-semibold',
                        {
                          'text-green-600': isAcceptable,
                          'text-red-600': !isAcceptable,
                        },
                      )}
                      onChange={(e) => {
                        setAdjustedSalaries((prev) => {
                          const newValue = parseInt(e.target.value, 10);

                          return {
                            ...prev,
                            [item.id]: newValue || 0,
                          };
                        });
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className={cn('font-semibold', {
                      'text-gray-800': isInEditMode,
                      'text-green-600': !isInEditMode && isAcceptable,
                      'text-red-600': !isInEditMode && !isAcceptable,
                    })}
                  >
                    <span>{formatCurrency(item.adjusted_salary)}</span>
                  </div>
                )}

                <span
                  className={cn(
                    'font-semibold',

                    {
                      'text-gray-800': isInEditMode,
                      'text-green-600': !isInEditMode && isAcceptable,
                      'text-red-600': !isInEditMode && !isAcceptable,
                    },
                  )}
                >
                  /yr
                </span>
              </div>
            );
          },
        },
      ]}
    />
  );
};
